/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import styles from '../styles/pages/GuestDashboardPage.module.css';
import IOCMResources from '../components/pageComponents/dashboard/guest/IocmResources';

import {
	validateFullName,
	validatePhoneNumber,
	formatPhoneNumber,
	validateAddress,
} from '../utils/validationSchema';

import MobileModal from '../components/common/MobileModal';
import PopupMobileModal from '../components/common/PopupMobileModal';

import InputField from '../components/controlVariations/InputField';
import Dropdown from '../components/controlVariations/Dropdown';
import MultiSelectDropdown from '../components/controlVariations/MultiSelectDropdown';
import SearchableDropdown from '../components/controlVariations/SearchableDropdown';

import { useHeader } from '../context/HeaderContext';
import { ReactComponent as PersonalDetails } from '../assets/icons/PersonalDetails.svg';
import { ReactComponent as ProfessionalDetails } from '../assets/icons/ProfessionalDetails.svg';
import { ReactComponent as CoachingExperience } from '../assets/icons/CoachingExperience.svg';
import { ReactComponent as Growth } from '../assets/icons/GrowthIcon.svg';
import TickMarkSuccess from '../assets/icons/TickMarkSuccess.png';
import { useCountries } from '../hooks/useCountries';
import { useTimezones } from '../hooks/useTimezones';
import { useUserProfile } from '../hooks/useUserProfile';
import { usePreference } from '../hooks/usePreference';
import { useNavigate } from 'react-router-dom';

const PersonalIcon = () => <PersonalDetails className={styles.iconBox} />;
const ProfessionalIcon = () => (
	<ProfessionalDetails className={styles.iconBox} />
);
const CoachingIcon = () => <CoachingExperience className={styles.iconBox} />;
const GrowthIcon = () => <Growth className={styles.iconBox} />;
const TickMarkSuccessIcon = () => <img src={TickMarkSuccess} alt="Success" />;

const MobilePersonalIcon = () => (
	<PersonalDetails className={styles.mobileIconBox} />
);
const MobileProfessionalIcon = () => (
	<ProfessionalDetails className={styles.mobileIconBox} />
);
const MobileCoachingIcon = () => (
	<CoachingExperience className={styles.mobileIconBox} />
);
const MobileGrowthIcon = () => <Growth className={styles.mobileIconBox} />;

const GuestDashboardPage: React.FC = () => {
	// const TEST_MODE_SHOW_BANNER = true;
	const navigate = useNavigate();
	const { setHeaderContent } = useHeader();
	// FORM STATES to check for mobile view
	const [isMobile, setIsMobile] = useState(false);
	// UI States
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [showQuestionnaire, setShowQuestionnaire] = useState(true);
	const [submissionError, setSubmissionError] = useState<string | null>(null);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [submitSucceeded, setSubmitSucceeded] = useState(false);
	const [isMobileModalOpen, setIsMobileModalOpen] = useState(true);
	const [step, setStep] = useState(1);
	const [showSuccess, setShowSuccess] = useState(false);
	const [showMobileSuccess, setShowMobileSuccess] = useState(false);
	const [showMobileVerification, setShowMobileVerification] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkMobile();
		window.addEventListener('resize', checkMobile);

		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	const getStepState = (dotStep: number) => {
		if (dotStep < step) return 'filled';
		if (dotStep === step) return 'active';
		return 'inactive';
	};

	// Auto-hide the error popup after 4 seconds
	useEffect(() => {
		if (submissionError) {
			const timer = setTimeout(() => {
				setSubmissionError(null);
			}, 4000);
			return () => clearTimeout(timer);
		}
	}, [submissionError]);

	const [isLocalPending, setIsLocalPending] = useState(false); // New state for immediate banner display

	const {
		profile,
		updateProfile,
		loading: profileLoading,
	} = useUserProfile();
	const { updatePreferences, loading: prefLoading } = usePreference();

	const { countries, loading: countriesLoading } = useCountries();
	const { timezones, loading: timezonesLoading } = useTimezones();

	const [form, setForm] = useState({
		fullname: '',
		email: '',
		location: '',
		country: '' as string,
		phone: '',
		timezone: '',
		organization: '',
		designation: '',
		credentials: '',
		yearsofexperience: '',
		areasOfInterest: [] as string[],
		coachingReasons: [] as string[],
		growthNeeds: [] as string[],
	});

	// VALIDATION FUNCTION
	const isStepValid = () => {
		const errors: Record<string, string> = {};

		if (step === 1) {
			const fullNameError = validateFullName(form.fullname);
			const phoneError = validatePhoneNumber(form.phone);
			const locationError = validateAddress(form.location);

			if (fullNameError) errors.fullname = fullNameError;
			if (phoneError) errors.phone = phoneError;
			if (locationError) errors.location = locationError;
		}
		// STEP 2 – Professional Details
		if (step === 2) {
			if (form.areasOfInterest.length === 0)
				errors.areasOfInterest = 'Select at least one area of interest';
			if (form.designation === 'Independent Coach' && !form.credentials) {
				errors.credentials =
					'Credentials are required for Independent Coach';
			}
		}

		// STEP 3 – Coaching Experience
		if (step === 3 && form.coachingReasons.length === 0) {
			errors.coachingReasons = 'Select at least one option';
		}

		// STEP 4 – Growth Needs
		if (step === 4 && form.growthNeeds.length === 0) {
			errors.growthNeeds = 'Select at least one option';
		}

		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const [errors] = useState<Record<string, string>>({});
	const userDataString = localStorage.getItem('user');

	useEffect(() => {
		if (
			profile?.areasofinterest &&
			profile.areasofinterest.length > 0 &&
			!isLocalPending
		) {
			setShowQuestionnaire(false);
		}
	}, [profile, isLocalPending]);

	useEffect(() => {
		if (profile?.email) {
			setForm(prev => ({ ...prev, email: profile.email }));
		}
	}, [profile?.email]);

	useEffect(() => {
		if (profile?.fullname) {
			setForm(prev => ({ ...prev, fullname: profile.fullname }));
		}
	}, [profile?.fullname]);
	// 2. Pre-fill and Header welcome message
	const firstName = profile?.fullname?.split(' ')[0];

	useEffect(() => {
		if (firstName) {
			setHeaderContent(<p>Welcome {firstName}</p>);
		}
	}, [firstName]);

	const shouldShowVerificationBanner =
		profile?.areasofinterest && profile.areasofinterest.length > 0;

	const handleInputChange = (field: string, value: any) => {
		let processedValue = value;

		// 📱 PHONE NUMBER HARD LIMIT (E.164)
		if (field === 'phone') {
			// Allow only + and digits
			processedValue = value.replace(/[^+\d]/g, '');

			// Ensure only ONE '+' at the beginning
			if (processedValue.includes('+')) {
				processedValue =
					'+' + processedValue.replace(/\+/g, '').replace(/^/, '');
			}

			// Enforce max 15 digits (excluding '+')
			const digitsOnly = processedValue.replace(/\D/g, '');
			if (digitsOnly.length > 15) {
				return; // 🚫 STOP INPUT
			}

			// Optional formatting (if you want)
			processedValue = formatPhoneNumber(processedValue);
		}

		setForm(prev => ({ ...prev, [field]: processedValue }));
		setFieldErrors(prev => ({ ...prev, [field]: '' }));

		// 🔍 Live validation
		if (field === 'fullname') {
			setFieldErrors(prev => ({
				...prev,
				fullname: validateFullName(processedValue) || '',
			}));
		}

		if (field === 'phone') {
			setFieldErrors(prev => ({
				...prev,
				phone: validatePhoneNumber(processedValue) || '',
			}));
		}

		if (field === 'location') {
			setFieldErrors(prev => ({
				...prev,
				location: validateAddress(processedValue) || '',
			}));
		}
	};

	useEffect(() => {
		if (form.designation !== 'Independent Coach') {
			setForm(prev => ({
				...prev,
				credentials: '', // or [] if it's a multi-select
			}));
		}
	}, [form.designation]);

	const handleCheckboxToggle = (
		field: 'coachingReasons' | 'growthNeeds',
		value: string
	) => {
		const current = [...form[field]];
		const index = current.indexOf(value);
		if (index > -1) current.splice(index, 1);
		else current.push(value);
		handleInputChange(field, current);
	};

	const handleNext = () => {
		if (!isStepValid()) return;
		setStep(prev => Math.min(prev + 1, 4));
	};

	const handleBack = () => setStep(prev => Math.max(prev - 1, 1));

	// Helper function to remove empty/null/undefined fields
	const cleanObject = (obj: Record<string, any>) => {
		const cleaned: Record<string, any> = {};
		Object.keys(obj).forEach(key => {
			const value = obj[key];

			// Only add to cleaned object if it's not "empty"
			if (
				value !== null &&
				value !== undefined &&
				value !== '' &&
				!(Array.isArray(value) && value.length === 0)
			) {
				cleaned[key] = value;
			}
		});
		return cleaned;
	};

	const handleSubmit = async () => {
		setSubmissionError(null);

		if (!isStepValid()) {
			setSubmissionError(null);
			return;
		}
		setIsLocalPending(true); // 🔒 LOCK UI FLOW
		if (isMobile) {
			// 📱 MOBILE FLOW
			// setIsMobileModalOpen(false);     // close questionnaire popup
			setShowMobileSuccess(true); // open success popup
			setShowQuestionnaire(false); // hide questionnaire section

			setTimeout(() => {
				setShowMobileSuccess(false); // auto-close success popup
				setShowQuestionnaire(false); // permanently hide questionnaire
				setIsMobileModalOpen(false);
				setIsLocalPending(true); // ✅ unlock UI flow
				// setShowMobileVerification(true); // ✅ show verification popup
			}, 3000);
		} else if (!isMobile) {
			// 🖥 DESKTOP FLOW
			setShowSuccess(true);

			setTimeout(() => {
				setShowSuccess(false);
				setShowQuestionnaire(false);
			}, 3000);
		}

		// Create the raw data objects
		const rawProfileData = {
			fullname: form.fullname,
			phonenumber: form.phone,
			location: form.location,
			country: form.country,
			organizationname: form.organization,
			designation: form.designation,
			yearsofexperience: form.yearsofexperience,
			areasofinterest: form.areasOfInterest,
			coachingexperience: form.coachingReasons,
			growthparameters: form.growthNeeds,
			metadata: {
				credentials: form.credentials,
			},
		};

		const rawPreferenceData = {
			timezone: form.timezone,
			language: 'en',
		};

		if (
			form.areasOfInterest.length === 0 ||
			form.coachingReasons.length === 0 ||
			form.growthNeeds.length === 0
		) {
			setSubmissionError(null);
			return;
		}

		// CLEAN the objects: remove empty strings and empty arrays
		const profileData = cleanObject(rawProfileData);
		const preferenceData = cleanObject(rawPreferenceData);

		try {
			// We use a "settled" approach so even if preferences fail (Route not found),
			// the profile update can still move us forward.
			if (Object.keys(profileData).length > 0) {
				await updateProfile(profileData);
			}

			try {
				if (Object.keys(preferenceData).length > 0) {
					await updatePreferences(preferenceData);
				}
			} catch (e) {
				console.error(
					'Preference update failed, but continuing flow...',
					e
				);
			}

			console.log('Profile Data to submit:', profileData);
			// Success: Switch content to Success Screen
			// setIsSubmitted(true);
			// setSubmitSucceeded(true);

			// setIsLocalPending(true);
			setShowSuccess(true); // show success card
			setIsLocalPending(true); // show banner later

			console.log('Profile and Preferences updated ');
			// Hide questionnaire section after 5 seconds to show banner
			setTimeout(() => {
				// setShowQuestionnaire(false);
				// setIsSubmitted(false);
				setShowSuccess(false);
				setIsMobileModalOpen(false); // ✅ close popup
				setShowQuestionnaire(false); // hide form AFTER success
			}, 3000);
		} catch (err: any) {
			setSubmitSucceeded(false);
			setIsSubmitted(false); //  PREVENT SUCCESS VIEW
			setSubmissionError(
				'Failed to submit. Please check your details and try again.'
			);
			console.error('Submission failed', err);
		}
	};

	if (countriesLoading || timezonesLoading)
		return <div className={styles.loading}>Loading data...</div>;

	const countryOptions = countries.map(c => c.label);
	const timezoneOptions = timezones.map(tz => tz.label);

	const MobileQuestionnaireContent = (
		<div className={styles.mobileCard}>
			<div className={styles.mobileFormLayout}>
				<div className={styles.mobileFormBody}>
					<div className={styles.mobileHeader}>
						<div className={styles.mobileStepTitleBox}>
							<p className={styles.mobileStepTitle}>
								{step === 1 && 'PERSONAL DETAILS'}
								{step === 2 && 'PROFESSIONAL DETAILS'}
								{step === 3 && 'COACHING EXPERIENCE'}
								{step === 4 && 'GROWTH WITH IOCM'}
							</p>

							{step === 1 && <MobilePersonalIcon />}
							{step === 2 && <MobileProfessionalIcon />}
							{step === 3 && <MobileCoachingIcon />}
							{step === 4 && <MobileGrowthIcon />}
						</div>

						<p className={styles.mobileStepDesc}>
							{step === 1 &&
								'Share your location and contact details'}
							{step === 2 &&
								'Tell us about your professional background'}
							{step === 3 && (
								<>
									What primarily brings you to IOCM at this
									point?{' '}
									<span className={styles.asterisk}>*</span>
								</>
							)}
							{step === 4 && (
								<>
									What can IOCM do which might be useful for
									you now?{' '}
									<span className={styles.asterisk}>*</span>
								</>
							)}
						</p>

						<p className={styles.stepMandatory}>
							{(step === 3 || step === 4) && (
								<>
									Select at least 1{' '}
									<span className={styles.asterisk}>*</span>
								</>
							)}
						</p>
					</div>

					<div className={styles.mobileFormContent}>
						{/* Submission Error Message */}
						{submissionError && (
							<div className={styles.errorAlert}>
								{submissionError}
							</div>
						)}

						{step === 1 && (
							<div className={styles.mobileGrid2x3}>
								<div className={styles.mobileInputGroup}>
									<label>
										Full Name{' '}
										<span className={styles.asterisk}>
											*
										</span>
									</label>
									<InputField
										type="text"
										placeholder={profile?.fullname}
										value={form.fullname}
										onChange={e =>
											handleInputChange(
												'fullname',
												e.target.value
											)
										}
									/>{' '}
									{fieldErrors.fullname && (
										<p className={styles.errorText}>
											{fieldErrors.fullname}
										</p>
									)}
								</div>
								<div className={styles.mobileInputGroup}>
									<label>Email Id</label>
									<InputField
										disabled={true}
										className={styles.disabledInput}
										type="email"
										placeholder="john.doe@gmail.com"
										value={form.email}
									/>
								</div>
								<div className={styles.mobileInputGroup}>
									<label>Location</label>
									<InputField
										type="text"
										placeholder="Address Line 1, City"
										value={form.location}
										onChange={e =>
											handleInputChange(
												'location',
												e.target.value
											)
										}
									/>{' '}
									{fieldErrors.location && (
										<p className={styles.errorText}>
											{fieldErrors.location}
										</p>
									)}
								</div>
								<div className={styles.mobileInputGroup}>
									<label>Country Name</label>
									<SearchableDropdown
										placeholder="Select Country"
										options={countryOptions}
										selected={form.country}
										onSelect={val =>
											handleInputChange('country', val)
										}
									/>
								</div>
								<div className={styles.mobileInputGroup}>
									<label className={styles.inputLabel}>
										Phone Number{' '}
										<span className={styles.hintText}>
											Include Country Code{' '}
										</span>{' '}
									</label>{' '}
									<InputField
										type="text"
										placeholder="+ 123 456 7890"
										value={form.phone}
										onChange={e =>
											handleInputChange(
												'phone',
												e.target.value
											)
										}
									/>
									{fieldErrors.phone && (
										<p className={styles.errorText}>
											{fieldErrors.phone}
										</p>
									)}
								</div>
								<div className={styles.mobileInputGroup}>
									<label>Time Zone</label>
									<SearchableDropdown
										placeholder="Select Timezone"
										options={timezoneOptions}
										selected={form.timezone}
										onSelect={val =>
											handleInputChange('timezone', val)
										}
										width="100%"
										height="58px"
									/>
								</div>
							</div>
						)}

						{step === 2 && (
							<div className={styles.mobileGrid2x2}>
								<div className={styles.mobileInputGroup}>
									<label>Organization Name</label>
									<InputField
										type="text"
										placeholder="Enter your Organization Name"
										value={form.organization}
										onChange={e =>
											handleInputChange(
												'organization',
												e.target.value
											)
										}
									/>
								</div>
								<div className={styles.mobileInputGroup}>
									<label>Current Professional Role</label>
									<Dropdown
										placeholder="Select Role"
										options={[
											'HR / L&D / OD',
											'Business Leader',
											'Independent Coach',
											'Entrepreneur',
											'Other',
										]}
										selected={form.designation}
										onSelect={value =>
											handleInputChange(
												'designation',
												value
											)
										}
										width="100%"
										height="58px"
										variant="default"
									/>
								</div>
								<div className={styles.mobileInputGroup}>
									<label>
										Total years of work experience
									</label>
									<Dropdown
										placeholder="0-5 years"
										options={[
											'0-5 years of experience',
											'5-10 years of experience',
											'10-15 years of experience',
											'15+ years of experience',
										]}
										selected={form.yearsofexperience}
										onSelect={value =>
											handleInputChange(
												'yearsofexperience',
												value
											)
										}
										width="100%"
										height="58px"
										variant="default"
									/>
								</div>
								<div className={styles.mobileInputGroup}>
									<label>
										Areas of Interest{' '}
										<span className={styles.asterisk}>
											*
										</span>
									</label>
									<MultiSelectDropdown
										placeholder="Select Areas of Interest"
										options={[
											'ICF Coaching Credentials',
											'Mentoring',
											'Coaching culture',
											'Leadership Workshops',
											'Psychological Safety',
											'Emotional Agility',
											'1 on 1 Coaching',
											'Group Coaching',
											'Team Coaching',
											'Research Benchmarking',
											'Assessments',
										]}
										selected={form.areasOfInterest}
										onChange={values =>
											handleInputChange(
												'areasOfInterest',
												values
											)
										}
									/>

									{fieldErrors.areasOfInterest && (
										<p className={styles.errorText}>
											{fieldErrors.areasOfInterest}
										</p>
									)}
								</div>

								<div className={styles.mobileInputGroup}>
									<label
										className={
											form.designation !==
											'Independent Coach'
												? styles.disabledLabel
												: ''
										}
									>
										Credentials
										{form.designation ===
											'Independent Coach' && (
											<span className={styles.asterisk}>
												{' '}
												*
											</span>
										)}
									</label>
									<Dropdown
										placeholder="Select Credentials"
										options={[
											'ACC',
											'PCC',
											'MCC',
											'EMCC',
											'No Credientials',
										]}
										selected={form.credentials}
										onSelect={value =>
											handleInputChange(
												'credentials',
												value
											)
										}
										width="100%"
										height="58px"
										variant="default"
										disabled={
											form.designation !==
											'Independent Coach'
										}
									/>
									{fieldErrors.credentials && (
										<p className={styles.errorText}>
											{fieldErrors.credentials}
										</p>
									)}
								</div>
							</div>
						)}

						{(step === 3 || step === 4) && (
							<>
								<div className={styles.mobileCheckboxGrid}>
									{(step === 3
										? [
												'Personal Growth',
												'Professional Certification',
												'Leadership Effectiveness',
												'Organisational Impact',
												'Career Transition',
												'Curiosity',
												'Building Coaching Capability',
											]
										: [
												'Clarity on if certification is right for me',
												'Creating Impact',
												'Understanding ICF pathways',
												'Designing Leadership Pathways',
												'Research & Thought Leadership',
												'Building Internal Coaching Capability',
												'Finding Purpose',
												'Executive Coaching',
											]
									).map(option => (
										<label
											key={option}
											className={styles.checkboxItem}
										>
											<span>{option}</span>
											<input
												type="checkbox"
												className={
													styles.customCheckbox
												}
												checked={
													step === 3
														? form.coachingReasons.includes(
																option
															)
														: form.growthNeeds.includes(
																option
															)
												}
												onChange={() =>
													handleCheckboxToggle(
														step === 3
															? 'coachingReasons'
															: 'growthNeeds',
														option
													)
												}
											/>
										</label>
									))}
								</div>

								{/* ✅ GROUP LEVEL ERROR MESSAGE */}
								{step === 3 && fieldErrors.coachingReasons && (
									<p className={styles.errorText}>
										{fieldErrors.coachingReasons}
									</p>
								)}

								{step === 4 && fieldErrors.growthNeeds && (
									<p className={styles.errorText}>
										{fieldErrors.growthNeeds}
									</p>
								)}
							</>
						)}
					</div>
				</div>

				<div className={styles.mobileFooter}>
					<div className={styles.mobileButtonGroup}>
						{step > 1 && (
							<button
								onClick={handleBack}
								className={styles.mobileBackBtn}
							>
								Back
							</button>
						)}
						{step < 4 ? (
							<button
								onClick={handleNext}
								className={styles.mobileNextBtn}
							>
								Next
							</button>
						) : (
							<button
								onClick={handleSubmit}
								className={styles.mobileNextBtn}
							>
								Submit
							</button>
						)}
					</div>

					<div className={styles.mobileStepIndicator}>
						{[1, 2, 3, 4].map(i => {
							const state = getStepState(i);
							return (
								<div
									key={i}
									className={`${styles.mobileDot} ${styles[state]}`}
								></div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className={styles.container}>
			{/* 1. THE RED ERROR POPUP */}
			{submissionError && (
				<div className={styles.errorPopup}>
					<span>⚠️ {submissionError}</span>
					<button
						className={styles.closePopup}
						onClick={() => setSubmissionError(null)}
					>
						×
					</button>
				</div>
			)}

			{/* 🖥 DESKTOP VERIFICATION BANNER */}
			{!isMobile &&
				!showQuestionnaire &&
				shouldShowVerificationBanner && (
					<div className={styles.verificationBanner}>
						<span className={styles.infoIcon}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="22"
								height="22"
								viewBox="0 0 22 22"
								fill="none"
							>
								<path
									d="M11 15V11M11 7H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
									stroke="white"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
						Your profile is being verified. Please explore our
						resources and events to know more about IOCM offerings.
					</div>
				)}

			{/* 📱 MOBILE VERIFICATION BANNER */}
			{isMobile && !showQuestionnaire && shouldShowVerificationBanner && (
				<div className={styles.mobileVerificationBanner}>
					<span className={styles.infoIcon}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="22"
							height="22"
							viewBox="0 0 22 22"
							fill="none"
						>
							<path
								d="M11 15V11M11 7H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
								stroke="white"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</span>
					<p>
						Your profile is being verified.
						<br />
						Please explore our resources and events to know more
						about IOCM offerings.
					</p>
				</div>
			)}

			{/* 2. QUESTIONNAIRE SECTION */}

			{isMobile && showQuestionnaire && (
				<div className={styles.mobileProfileCard}>
					{isMobile && (
						<MobileModal
							open={isMobileModalOpen}
							onClose={() => setIsMobileModalOpen(false)}
						>
							{MobileQuestionnaireContent}
						</MobileModal>
					)}
					<div className={styles.mobilePageHeader}>
						<p className={styles.mobileTitle}>
							LET’S GET YOU STARTED
						</p>
						<p className={styles.mobileDesc}>
							Answer these questions to help us understand you
							better
						</p>
					</div>

					<div className={styles.mobileProgressRow}>
						<div className={styles.mobileProgressLabel}>
							{' '}
							<span>Profile Completion</span>
							<span className={styles.mobilePercentText}>
								{step * 25}%
							</span>{' '}
						</div>

						<div className={styles.mobileProgressBar}>
							<div
								className={styles.mobileProgressFill}
								style={{ width: `${step * 25}%` }}
							/>
						</div>
					</div>

					<button
						className={styles.completeProfileBtn}
						onClick={() => setIsMobileModalOpen(true)}
					>
						Complete Profile
					</button>
				</div>
			)}
			{showQuestionnaire && !isMobile && (
				<>
					<div className={styles.headerRow}>
						<div className={styles.leftSideHeaderRow}>
							<span>LET'S GET YOU STARTED</span>
							<p className={styles.leftSideDesc}>
								Answer these questions to help us understand you
								better
							</p>
						</div>
						<div className={styles.rightSideHeaderRow}>
							<div className={styles.progressLabel}>
								<span>Profile Completion</span>
								<span className={styles.percentText}>
									{step * 25}%
								</span>
							</div>
							<div className={styles.progressBar}>
								<div
									className={styles.progressFill}
									style={{ width: `${step * 25}%` }}
								></div>
							</div>
						</div>
					</div>

					<div className={styles.card}>
						{/* {isSubmitted && submitSucceeded ? ( */}
						{showSuccess ? (
							/* SUCCESS SCREEN (Replaces content inside the card) */
							<div className={styles.successView}>
								<TickMarkSuccessIcon />
								<h2>Thank you for your responses!</h2>
								<p>
									We will review your profile and assign a
									role based on your responses.
								</p>
							</div>
						) : (
							/* MAIN FORM LAYOUT */
							<div className={styles.formLayout}>
								<div className={styles.formBody}>
									<div className={styles.sidebar}>
										{step === 1 && <PersonalIcon />}
										{step === 2 && <ProfessionalIcon />}
										{step === 3 && <CoachingIcon />}
										{step === 4 && <GrowthIcon />}

										<p className={styles.stepTitle}>
											{step === 1 && 'PERSONAL DETAILS'}
											{step === 2 &&
												'PROFESSIONAL DETAILS'}
											{step === 3 &&
												'COACHING EXPERIENCE'}
											{step === 4 && 'GROWTH WITH IOCM'}
										</p>
										<p className={styles.stepDesc}>
											{step === 1 &&
												'Share your location and contact details'}
											{step === 2 &&
												'Tell us about your professional background'}
											{step === 3 && (
												<>
													What primarily brings you to
													IOCM at this point?{' '}
													<span
														className={
															styles.asterisk
														}
													>
														*
													</span>
												</>
											)}
											{step === 4 && (
												<>
													What can IOCM do which might
													be useful for you now?{' '}
													<span
														className={
															styles.asterisk
														}
													>
														*
													</span>
												</>
											)}
										</p>
										<p className={styles.stepMandatory}>
											{(step === 3 || step === 4) && (
												<>
													Select at least 1{' '}
													<span
														className={
															styles.asterisk
														}
													>
														*
													</span>
												</>
											)}
										</p>
									</div>

									<div className={styles.formContent}>
										{/* Submission Error Message */}
										{submissionError && (
											<div className={styles.errorAlert}>
												{submissionError}
											</div>
										)}

										{step === 1 && (
											<div className={styles.grid2x3}>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>
														Full Name{' '}
														<span
															className={
																styles.asterisk
															}
														>
															*
														</span>
													</label>
													<InputField
														type="text"
														placeholder={
															profile?.fullname
														}
														value={form.fullname}
														onChange={e =>
															handleInputChange(
																'fullname',
																e.target.value
															)
														}
													/>{' '}
													{fieldErrors.fullname && (
														<p
															className={
																styles.errorText
															}
														>
															{
																fieldErrors.fullname
															}
														</p>
													)}
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>Email Id</label>
													<InputField
														disabled={true}
														className={
															styles.disabledInput
														}
														type="email"
														placeholder="john.doe@gmail.com"
														value={form.email}
													/>
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>Location</label>
													<InputField
														type="text"
														placeholder="Address Line 1, City"
														value={form.location}
														onChange={e =>
															handleInputChange(
																'location',
																e.target.value
															)
														}
													/>{' '}
													{fieldErrors.location && (
														<p
															className={
																styles.errorText
															}
														>
															{
																fieldErrors.location
															}
														</p>
													)}
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>Country Name</label>
													<SearchableDropdown
														placeholder="Select Country"
														options={countryOptions}
														selected={form.country}
														onSelect={val =>
															handleInputChange(
																'country',
																val
															)
														}
													/>
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label
														className={
															styles.inputLabel
														}
													>
														Phone Number{' '}
														<span
															className={
																styles.hintText
															}
														>
															Include Country
															Code{' '}
														</span>{' '}
													</label>{' '}
													<InputField
														type="text"
														placeholder="+ 123 456 7890"
														value={form.phone}
														onChange={e =>
															handleInputChange(
																'phone',
																e.target.value
															)
														}
													/>
													{fieldErrors.phone && (
														<p
															className={
																styles.errorText
															}
														>
															{fieldErrors.phone}
														</p>
													)}
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>Time Zone</label>
													<SearchableDropdown
														placeholder="Select Timezone"
														options={
															timezoneOptions
														}
														selected={form.timezone}
														onSelect={val =>
															handleInputChange(
																'timezone',
																val
															)
														}
														width="100%"
														height="58px"
													/>
												</div>
											</div>
										)}

										{step === 2 && (
											<div className={styles.grid2x2}>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>
														Organization Name
													</label>
													<InputField
														type="text"
														placeholder="Enter your Organization Name"
														value={
															form.organization
														}
														onChange={e =>
															handleInputChange(
																'organization',
																e.target.value
															)
														}
													/>
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>
														Current Professional
														Role
													</label>
													<Dropdown
														placeholder="Select Role"
														options={[
															'HR / L&D / OD',
															'Business Leader',
															'Independent Coach',
															'Entrepreneur',
															'Other',
														]}
														selected={
															form.designation
														}
														onSelect={value =>
															handleInputChange(
																'designation',
																value
															)
														}
														width="100%"
														height="58px"
														variant="default"
													/>
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>
														Total years of work
														experience
													</label>
													<Dropdown
														placeholder="0-5 years"
														options={[
															'0-5 years of experience',
															'5-10 years of experience',
															'10-15 years of experience',
															'15+ years of experience',
														]}
														selected={
															form.yearsofexperience
														}
														onSelect={value =>
															handleInputChange(
																'yearsofexperience',
																value
															)
														}
														width="100%"
														height="58px"
														variant="default"
													/>
												</div>
												<div
													className={
														styles.inputGroup
													}
												>
													<label>
														Areas of Interest{' '}
														<span
															className={
																styles.asterisk
															}
														>
															*
														</span>
													</label>
													<MultiSelectDropdown
														placeholder="Select Areas of Interest"
														options={[
															'ICF Coaching Credentials',
															'Mentoring',
															'Coaching culture',
															'Leadership Workshops',
															'Psychological Safety',
															'Emotional Agility',
															'1 on 1 Coaching',
															'Group Coaching',
															'Team Coaching',
															'Research Benchmarking',
															'Assessments',
														]}
														selected={
															form.areasOfInterest
														}
														onChange={values =>
															handleInputChange(
																'areasOfInterest',
																values
															)
														}
													/>

													{fieldErrors.areasOfInterest && (
														<p
															className={
																styles.errorText
															}
														>
															{
																fieldErrors.areasOfInterest
															}
														</p>
													)}
												</div>

												<div
													className={
														styles.inputGroup
													}
												>
													<label
														className={
															form.designation !==
															'Independent Coach'
																? styles.disabledLabel
																: ''
														}
													>
														Credentials
														{form.designation ===
															'Independent Coach' && (
															<span
																className={
																	styles.asterisk
																}
															>
																{' '}
																*
															</span>
														)}
													</label>
													<Dropdown
														placeholder="Select Credentials"
														options={[
															'ACC',
															'PCC',
															'MCC',
															'EMCC',
															'No Credientials',
														]}
														selected={
															form.credentials
														}
														onSelect={value =>
															handleInputChange(
																'credentials',
																value
															)
														}
														width="100%"
														height="58px"
														variant="default"
														disabled={
															form.designation !==
															'Independent Coach'
														}
													/>
													{fieldErrors.credentials && (
														<p
															className={
																styles.errorText
															}
														>
															{
																fieldErrors.credentials
															}
														</p>
													)}
												</div>
											</div>
										)}

										{(step === 3 || step === 4) && (
											<>
												<div
													className={
														styles.checkboxGrid
													}
												>
													{(step === 3
														? [
																'Personal Growth',
																'Professional Certification',
																'Leadership Effectiveness',
																'Organisational Impact',
																'Career Transition',
																'Curiosity',
																'Building Coaching Capability',
															]
														: [
																'Clarity on if certification is right for me',
																'Creating Impact',
																'Understanding ICF pathways',
																'Designing Leadership Pathways',
																'Research & Thought Leadership',
																'Building Internal Coaching Capability',
																'Finding Purpose',
																'Executive Coaching',
															]
													).map(option => (
														<label
															key={option}
															className={
																styles.checkboxItem
															}
														>
															<span>
																{option}
															</span>
															<input
																type="checkbox"
																className={
																	styles.customCheckbox
																}
																checked={
																	step === 3
																		? form.coachingReasons.includes(
																				option
																			)
																		: form.growthNeeds.includes(
																				option
																			)
																}
																onChange={() =>
																	handleCheckboxToggle(
																		step ===
																			3
																			? 'coachingReasons'
																			: 'growthNeeds',
																		option
																	)
																}
															/>
														</label>
													))}
												</div>

												{/* ✅ GROUP LEVEL ERROR MESSAGE */}
												{step === 3 &&
													fieldErrors.coachingReasons && (
														<p
															className={
																styles.errorText
															}
														>
															{
																fieldErrors.coachingReasons
															}
														</p>
													)}

												{step === 4 &&
													fieldErrors.growthNeeds && (
														<p
															className={
																styles.errorText
															}
														>
															{
																fieldErrors.growthNeeds
															}
														</p>
													)}
											</>
										)}
									</div>
								</div>
								<div className={styles.footer}>
									<div className={styles.stepIndicator}>
										{[1, 2, 3, 4].map(i => {
											const state = getStepState(i);

											return (
												<div
													key={i}
													className={`${styles.dot} ${styles[state]}`}
												>
													{i}
												</div>
											);
										})}
									</div>

									<div className={styles.buttonGroup}>
										{step > 1 && (
											<button
												onClick={handleBack}
												className={styles.backBtn}
												disabled={
													profileLoading ||
													prefLoading
												}
											>
												Back
											</button>
										)}
										{step < 4 ? (
											<button
												onClick={handleNext}
												className={styles.nextBtn}
											>
												Next
											</button>
										) : (
											<button
												onClick={handleSubmit}
												className={styles.nextBtn}
												disabled={
													profileLoading ||
													prefLoading
												}
											>
												{profileLoading || prefLoading
													? 'Submitting...'
													: 'Submit'}
											</button>
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</>
			)}

			{/* 📱 MOBILE SUCCESS POPUP (already added earlier) */}
			{isMobile && showMobileSuccess && (
				<PopupMobileModal
					open={showMobileSuccess}
					onClose={() => setShowMobileSuccess(false)}
				>
					<div className={styles.mobileSuccessPopup}>
						<TickMarkSuccessIcon />
						<h3>Thank you for your responses!</h3>
						<p>
							We will assess your profile and your responses to
							assign a suitable role..
						</p>
					</div>
				</PopupMobileModal>
			)}

			{/* 📱 MOBILE VERIFICATION POPUP ← PUT IT HERE */}
			{isMobile && showMobileVerification && (
				<MobileModal
					open={showMobileVerification}
					onClose={() => setShowMobileVerification(false)}
				>
					<div className={styles.mobileVerificationPopup}>
						<span className={styles.infoIcon}>
							{' '}
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="22"
								height="22"
								viewBox="0 0 22 22"
								fill="none"
							>
								<path
									d="M11 15V11M11 7H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
									stroke="white"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
						<p>
							Your profile is being verified.
							<br />
							Please explore our resources and events to know more
							about IOCM offerings.
						</p>
					</div>
				</MobileModal>
			)}
			{/* 3. PERSISTENT COMPONENTS */}
			<IOCMResources />
		</div>
	);
};

export default GuestDashboardPage;
